import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchBacktestUnified } from '../api';

const ENTRY_TYPES = [
  { value: 'atm_straddle', label: 'ATM Straddle' },
  { value: 'dynamic_atm_straddle', label: 'Dynamic ATM Straddle (market-aware roll)' },
  { value: 'opening_range', label: 'Opening Range (Theta)' },
  { value: 'rolling_strangle', label: 'Rolling Strangle' },
  { value: 'ladder_straddle', label: 'Ladder Straddle' },
];

const INDEXES = ['Nifty', 'BankNifty', 'Sensex'];
const DAYS = ['Daily', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const SL_TYPES = [
  { value: 'none', label: 'None' },
  { value: 'spot', label: 'Spot Bounds' },
  { value: 'premium', label: 'Premium Threshold' },
  { value: 'amount', label: 'Amount (₹)' },
];

function getDefaultDate() {
  const d = new Date(); d.setDate(d.getDate() - 1);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
}

const DEFAULT_CFG = {
  entry_type: 'atm_straddle',
  index: 'Nifty',
  trading_day: 'Daily',
  adjustment_points: 50,
  strike_interval: 50,
  start_time: '09:20',
  exit_time: '14:40',
  lots: 1,
  strike_mode: 'ATM',
  itm_points: 0,
  has_breakout: false,
  sl_type: 'none',
  sl_lower: 0,
  sl_upper: 0,
  sl_premium: 0,
  sl_amount: 0,
  offset_points: 500,
  rolling_points: 300,
  // Dynamic ATM straddle thresholds
  // Dynamic ATM Straddle — strict-AND rule (tunable in backtest only)
  dyn_premium_ratio: 2.0,
  dyn_mtm_drawdown_pct: 30,
  dyn_roll_start_time: '09:55',
  dyn_roll_end_time: '14:00',
  dyn_cooldown_minutes: 10,
  dyn_max_rolls: 2,
  dyn_profit_lock_pct: 20,
  dyn_st_flip_fresh_min: 5,
};

export default function Backtest() {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState({ ...DEFAULT_CFG });
  const [fromDate, setFromDate] = useState(getDefaultDate);
  const [toDate, setToDate] = useState(getDefaultDate);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedDay, setExpandedDay] = useState(null);

  const set = (key, val) => setCfg(prev => ({ ...prev, [key]: val }));

  const isStrangle = cfg.entry_type === 'rolling_strangle';
  const isLadder = cfg.entry_type === 'ladder_straddle';
  const isDynamic = cfg.entry_type === 'dynamic_atm_straddle';

  const runBacktest = useCallback(async () => {
    setLoading(true);
    setError(null);
    setData(null);
    setExpandedDay(null);
    try {
      const params = { ...cfg, from_date: fromDate, to_date: toDate };
      if (cfg.entry_type === 'opening_range') params.has_breakout = true;
      const res = await fetchBacktestUnified(params);
      if (res.data?.status === 'ok') {
        setData(res.data);
      } else {
        setError(res.data?.message || 'Backtest failed');
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to run backtest');
    }
    setLoading(false);
  }, [cfg, fromDate, toDate]);

  const exportCSV = () => {
    if (!data) return;
    const lotSize = data.lot_size || 1;
    const rows = [['Date', 'Open', 'Close', 'Range', 'Trades', 'Adj', 'P&L/Lot', 'P&L Total',
      'Trade#', 'CE Strike', 'PE Strike', 'Entry', 'CE Sell', 'PE Sell', 'Exit', 'CE Buy', 'PE Buy',
      'Reason', 'CE P&L', 'PE P&L', 'Total (pts)', 'P&L (₹)', 'Cumulative (₹)']];
    data.days?.forEach(day => {
      if (day.status !== 'ok') { rows.push([day.date, '', '', '', '', '', '', '', day.message || 'No data']); return; }
      const s = day.summary;
      if (day.trades?.length > 0) {
        let cumPnl = 0;
        day.trades.forEach((t, i) => {
          cumPnl += (t.total_pnl || 0);
          rows.push([
            i === 0 ? day.date : '', i === 0 ? s.nifty_open : '', i === 0 ? s.nifty_close : '',
            i === 0 ? s.nifty_range?.toFixed(0) : '', i === 0 ? s.total_trades : '',
            i === 0 ? s.total_adjustments : '', i === 0 ? s.pnl_per_lot?.toFixed(2) : '',
            i === 0 ? s.pnl_total : '',
            t.trade_no, t.ce_strike || t.strike, t.pe_strike || t.strike,
            t.entry_time,
            t.ce_entry ? t.ce_entry.toFixed(2) : '—', t.pe_entry ? t.pe_entry.toFixed(2) : '—',
            t.exit_time,
            t.ce_exit ? t.ce_exit.toFixed(2) : '—', t.pe_exit ? t.pe_exit.toFixed(2) : '—',
            t.exit_reason,
            t.ce_entry ? t.ce_pnl?.toFixed(2) : '—', t.pe_entry ? t.pe_pnl?.toFixed(2) : '—',
            t.total_pnl?.toFixed(2),
            Math.round((t.total_pnl || 0) * lotSize), Math.round(cumPnl * lotSize),
          ]);
        });
      } else {
        rows.push([day.date, s.nifty_open, s.nifty_close, s.nifty_range?.toFixed(0), s.total_trades, s.total_adjustments, s.pnl_per_lot?.toFixed(2), s.pnl_total]);
      }
    });
    const a = data.aggregate;
    rows.push([]);
    rows.push(['SUMMARY', 'Days:', a.trading_days, 'WinRate:', a.win_rate + '%', 'Trades:', a.total_trades, '', 'P&L/Lot:', a.pnl_per_lot?.toFixed(2), 'Total:', a.pnl_total, 'Avg:', a.avg_pnl_per_day?.toFixed(2)]);
    const csv = rows.map(r => r.map(v => `"${v ?? ''}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url; link.download = `backtest_${cfg.entry_type}_${fromDate}_to_${toDate}.csv`;
    link.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="page-backtest">
      <h1 className="page-title">History</h1>
      <p className="page-subtitle">Simulate strategies with production-equivalent settings</p>

      <div className="hub-tabs">
        <button className="hub-tab" onClick={() => navigate('/trade-history')}>Trade History</button>
        <button className="hub-tab hub-tab-active">Backtest</button>
      </div>

      <div className="bt-controls-panel">
        {/* Row 1: Strategy type + Index + Day */}
        <div className="bt-config-row">
          <div className="bt-field">
            <label>Strategy Type</label>
            <select value={cfg.entry_type} onChange={e => set('entry_type', e.target.value)}>
              {ENTRY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div className="bt-field">
            <label>Index</label>
            <select
              value={cfg.index}
              onChange={e => {
                const nextIndex = e.target.value;
                set('index', nextIndex);
                if (nextIndex === 'BankNifty') {
                  set('strike_interval', 100);
                  if (!isStrangle && !isLadder) set('adjustment_points', 100);
                }
              }}
            >
              {INDEXES.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <div className="bt-field">
            <label>Trading Day</label>
            <select value={cfg.trading_day} onChange={e => set('trading_day', e.target.value)}>
              {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="bt-field">
            <label>Lots</label>
            <input type="number" min={1} max={100} value={cfg.lots} onChange={e => set('lots', parseInt(e.target.value) || 1)} />
          </div>
        </div>

        {/* Row 2: Timing + Strike */}
        <div className="bt-config-row">
          <div className="bt-field">
            <label>Start Time</label>
            <input type="time" value={cfg.start_time} onChange={e => set('start_time', e.target.value)} />
          </div>
          <div className="bt-field">
            <label>Exit Time</label>
            <input type="time" value={cfg.exit_time} onChange={e => set('exit_time', e.target.value)} />
          </div>
          {!isStrangle && !isLadder && (
            <>
              {!isDynamic && (
                <div className="bt-field">
                  <label>Adjustment (pts)</label>
                  <input type="number" min={10} step={10} value={cfg.adjustment_points} onChange={e => set('adjustment_points', parseInt(e.target.value) || 50)} />
                </div>
              )}
              <div className="bt-field">
                <label>Strike Interval</label>
                <input type="number" min={25} step={25} value={cfg.strike_interval} onChange={e => set('strike_interval', parseInt(e.target.value) || 50)} />
              </div>
            </>
          )}
          {isStrangle && (
            <>
              <div className="bt-field">
                <label>Offset (pts)</label>
                <input type="number" min={100} step={100} value={cfg.offset_points} onChange={e => set('offset_points', parseInt(e.target.value) || 500)} />
              </div>
              <div className="bt-field">
                <label>Rolling Trigger (pts)</label>
                <input type="number" min={50} step={50} value={cfg.rolling_points} onChange={e => set('rolling_points', parseInt(e.target.value) || 300)} />
              </div>
              <div className="bt-field">
                <label>Strike Interval</label>
                <input type="number" min={25} step={25} value={cfg.strike_interval} onChange={e => set('strike_interval', parseInt(e.target.value) || 100)} />
              </div>
            </>
          )}
          {isLadder && (
            <>
              <div className="bt-field">
                <label>Offset (pts)</label>
                <input type="number" min={50} step={50} value={cfg.offset_points} onChange={e => set('offset_points', parseInt(e.target.value) || 100)} />
              </div>
              <div className="bt-field">
                <label>Rolling Trigger (pts)</label>
                <input type="number" min={50} step={50} value={cfg.rolling_points} onChange={e => set('rolling_points', parseInt(e.target.value) || 300)} />
              </div>
              <div className="bt-field">
                <label>Strike Interval</label>
                <input type="number" min={25} step={25} value={cfg.strike_interval} onChange={e => set('strike_interval', parseInt(e.target.value) || 100)} />
              </div>
            </>
          )}
        </div>

        {/* Dynamic ATM straddle — strict-AND rule (tunable for backtest only) */}
        {isDynamic && (
          <>
            <div className="bt-config-row" style={{ marginTop: 8, padding: '8px 10px', background: '#0d1117', border: '1px solid #21262d', borderRadius: 6 }}>
              <div style={{ width: '100%', color: '#7ee787', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                🧠 Dynamic Roll — Triggers (BOTH must fire)
              </div>
              <div className="bt-field"><label title="tested-leg / untested-leg premium ratio">Premium Ratio ≥</label>
                <input type="number" min={1.2} step={0.1} value={cfg.dyn_premium_ratio} onChange={e => set('dyn_premium_ratio', parseFloat(e.target.value) || 2.0)} />
              </div>
              <div className="bt-field"><label title="combined MTM drawdown % of opening credit">MTM Drawdown %</label>
                <input type="number" min={5} step={5} value={cfg.dyn_mtm_drawdown_pct} onChange={e => set('dyn_mtm_drawdown_pct', parseFloat(e.target.value) || 30)} />
              </div>
            </div>
            <div className="bt-config-row" style={{ marginTop: 4, padding: '8px 10px', background: '#0d1117', border: '1px solid #21262d', borderRadius: 6 }}>
              <div style={{ width: '100%', color: '#58a6ff', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                ✅ Confirmation (EMA9-stack OR fresh Supertrend flip)
              </div>
              <div className="bt-field"><label title="Supertrend flip considered fresh for N minutes after the flip">ST Flip Fresh (min)</label>
                <input type="number" min={1} step={1} value={cfg.dyn_st_flip_fresh_min} onChange={e => set('dyn_st_flip_fresh_min', parseInt(e.target.value) || 5)} />
              </div>
            </div>
            <div className="bt-config-row" style={{ marginTop: 4, padding: '8px 10px', background: '#0d1117', border: '1px solid #21262d', borderRadius: 6 }}>
              <div style={{ width: '100%', color: '#f0883e', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                🛑 Vetoes (ALL must pass)
              </div>
              <div className="bt-field"><label title="only roll inside this window">Roll Window</label>
                <div style={{ display: 'flex', gap: 4 }}>
                  <input type="time" value={cfg.dyn_roll_start_time} onChange={e => set('dyn_roll_start_time', e.target.value)} />
                  <input type="time" value={cfg.dyn_roll_end_time} onChange={e => set('dyn_roll_end_time', e.target.value)} />
                </div>
              </div>
              <div className="bt-field"><label title="min minutes between consecutive rolls">Cooldown (min)</label>
                <input type="number" min={0} step={1} value={cfg.dyn_cooldown_minutes} onChange={e => set('dyn_cooldown_minutes', parseInt(e.target.value) || 0)} />
              </div>
              <div className="bt-field"><label title="max number of rolls in a single trading day">Max Rolls/Day</label>
                <input type="number" min={0} step={1} value={cfg.dyn_max_rolls} onChange={e => set('dyn_max_rolls', parseInt(e.target.value) || 0)} />
              </div>
              <div className="bt-field"><label title="skip roll if combined position is already +X% in profit">Profit Lock %</label>
                <input type="number" min={0} step={5} value={cfg.dyn_profit_lock_pct} onChange={e => set('dyn_profit_lock_pct', parseFloat(e.target.value) || 0)} />
              </div>
            </div>
          </>
        )}

        {/* Row 3: Strike mode + SL */}
        {!isStrangle && !isLadder && (
          <div className="bt-config-row">
            <div className="bt-field">
              <label>Strike Mode</label>
              <select value={cfg.strike_mode} onChange={e => set('strike_mode', e.target.value)}>
                <option value="ATM">ATM</option>
                <option value="ITM">ITM</option>
              </select>
            </div>
            {cfg.strike_mode === 'ITM' && (
              <div className="bt-field">
                <label>ITM Points</label>
                <input type="number" min={0} step={50} value={cfg.itm_points} onChange={e => set('itm_points', parseInt(e.target.value) || 0)} />
              </div>
            )}
            <div className="bt-field">
              <label>Stop Loss</label>
              <select value={cfg.sl_type} onChange={e => set('sl_type', e.target.value)}>
                {SL_TYPES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            {cfg.sl_type === 'spot' && (
              <>
                <div className="bt-field">
                  <label>SL Lower</label>
                  <input type="number" value={cfg.sl_lower} onChange={e => set('sl_lower', parseFloat(e.target.value) || 0)} />
                </div>
                <div className="bt-field">
                  <label>SL Upper</label>
                  <input type="number" value={cfg.sl_upper} onChange={e => set('sl_upper', parseFloat(e.target.value) || 0)} />
                </div>
              </>
            )}
            {cfg.sl_type === 'premium' && (
              <div className="bt-field">
                <label>Premium SL (₹)</label>
                <input type="number" value={cfg.sl_premium} onChange={e => set('sl_premium', parseFloat(e.target.value) || 0)} />
              </div>
            )}
            {cfg.sl_type === 'amount' && (
              <div className="bt-field">
                <label>Amount SL (₹)</label>
                <input type="number" value={cfg.sl_amount} onChange={e => set('sl_amount', parseFloat(e.target.value) || 0)} />
              </div>
            )}
          </div>
        )}

        {/* Row 4: Date range + Run */}
        <div className="bt-config-row bt-run-row">
          <div className="bt-field">
            <label>From</label>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} max={new Date().toISOString().split('T')[0]} />
          </div>
          <div className="bt-field">
            <label>To</label>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} max={new Date().toISOString().split('T')[0]} />
          </div>
          <button className="bt-run-btn" onClick={runBacktest} disabled={loading}>
            {loading ? 'Running...' : 'Run Backtest'}
          </button>
          {data && <button className="bt-export-btn" onClick={exportCSV}>Export CSV</button>}
        </div>
      </div>

      {loading && (
        <div className="loading">
          <div className="spinner" />
          <p>Running backtest... Each day takes ~30s due to AngelOne API rate limits.</p>
        </div>
      )}

      {error && <div className="error-banner">{error}</div>}

      {data && (
        <div className="bt-results">
          {/* Aggregate Summary */}
          <div className="bt-summary">
            <div className="bt-summary-card">
              <span className="bt-label">Period</span>
              <span className="bt-val">{data.from_date} → {data.to_date}</span>
            </div>
            <div className="bt-summary-card highlight">
              <span className="bt-label">Trading Days</span>
              <span className="bt-val big">{data.aggregate.trading_days}</span>
            </div>
            <div className="bt-summary-card highlight pnl-pos">
              <span className="bt-label">Winning Days</span>
              <span className="bt-val big">{data.aggregate.winning_days}</span>
            </div>
            <div className="bt-summary-card highlight pnl-neg">
              <span className="bt-label">Losing Days</span>
              <span className="bt-val big">{data.aggregate.losing_days}</span>
            </div>
            <div className="bt-summary-card highlight">
              <span className="bt-label">Win Rate</span>
              <span className="bt-val big">{data.aggregate.win_rate}%</span>
            </div>
            <div className="bt-summary-card highlight">
              <span className="bt-label">Total Trades</span>
              <span className="bt-val big">{data.aggregate.total_trades}</span>
            </div>
            <div className={`bt-summary-card highlight ${data.aggregate.pnl_per_lot >= 0 ? 'pnl-pos' : 'pnl-neg'}`}>
              <span className="bt-label">Total P&L / Lot</span>
              <span className="bt-val big">{data.aggregate.pnl_per_lot >= 0 ? '+' : ''}{data.aggregate.pnl_per_lot?.toFixed(2)}</span>
            </div>
            <div className={`bt-summary-card highlight ${data.aggregate.pnl_total >= 0 ? 'pnl-pos' : 'pnl-neg'}`}>
              <span className="bt-label">Total P&L (₹)</span>
              <span className="bt-val big">₹{data.aggregate.pnl_total >= 0 ? '+' : ''}{data.aggregate.pnl_total?.toLocaleString('en-IN')}</span>
            </div>
            <div className={`bt-summary-card ${data.aggregate.avg_pnl_per_day >= 0 ? 'pnl-pos' : 'pnl-neg'}`}>
              <span className="bt-label">Avg P&L / Day</span>
              <span className="bt-val">{data.aggregate.avg_pnl_per_day >= 0 ? '+' : ''}{data.aggregate.avg_pnl_per_day?.toFixed(2)}</span>
            </div>
          </div>

          {/* Daily Breakdown */}
          <div className="bt-day-table">
            <h3>Daily Breakdown</h3>
            <div className="table-wrapper">
              <table className="bt-trade-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Open</th>
                    <th>Close</th>
                    <th>Range</th>
                    <th>Trades</th>
                    <th>Adj</th>
                    <th>P&L/Lot</th>
                    <th>P&L (₹)</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {data.days?.map((day) => (
                    <React.Fragment key={day.date}>
                      <tr
                        className={`day-row ${day.status !== 'ok' ? 'day-error' : day.summary?.pnl_per_lot >= 0 ? 'day-win' : 'day-loss'}`}
                        onClick={() => setExpandedDay(expandedDay === day.date ? null : day.date)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td><strong>{day.date}</strong></td>
                        {day.status === 'ok' ? (
                          <>
                            <td>{day.summary.nifty_open?.toLocaleString('en-IN')}</td>
                            <td>{day.summary.nifty_close?.toLocaleString('en-IN')}</td>
                            <td>{day.summary.nifty_range?.toFixed(0)}</td>
                            <td>{day.summary.total_trades}</td>
                            <td>{day.summary.total_adjustments}</td>
                            <td className={day.summary.pnl_per_lot >= 0 ? 'positive' : 'negative'}>
                              <strong>{day.summary.pnl_per_lot >= 0 ? '+' : ''}{day.summary.pnl_per_lot?.toFixed(2)}</strong>
                            </td>
                            <td className={day.summary.pnl_total >= 0 ? 'positive' : 'negative'}>
                              ₹{day.summary.pnl_total >= 0 ? '+' : ''}{day.summary.pnl_total?.toLocaleString('en-IN')}
                            </td>
                            <td className="expand-icon">{expandedDay === day.date ? '▼' : '▶'}</td>
                          </>
                        ) : (
                          <>
                            <td colSpan="7" className="day-error-msg">{day.message || 'No data available'}</td>
                            <td></td>
                          </>
                        )}
                      </tr>
                      {expandedDay === day.date && day.status === 'ok' && (
                        <tr className="expanded-row">
                          <td colSpan="9">
                            <div className="bt-expanded-detail">
                              {/* Events log */}
                              {day.events?.length > 0 && (
                                <div className="bt-events-log">
                                  <strong>Events:</strong>
                                  <div className="bt-events-list">
                                    {day.events.map((ev, i) => (
                                      <div key={i} className={`bt-event bt-event-${ev.type?.toLowerCase()}`}>
                                        <span className="bt-ev-time">{ev.time}</span>
                                        <span className="bt-ev-type">{ev.type}</span>
                                        <span className="bt-ev-msg">{ev.message}</span>
                                        {ev.detail && <small className="bt-ev-detail">{ev.detail}</small>}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              <table className="bt-trade-table inner-table">
                                <thead>
                                  <tr>
                                    <th>#</th>
                                    <th>CE Strike</th>
                                    <th>PE Strike</th>
                                    <th>Entry</th>
                                    <th>CE Sell ₹</th>
                                    <th>PE Sell ₹</th>
                                    <th>Exit</th>
                                    <th>CE Buy ₹</th>
                                    <th>PE Buy ₹</th>
                                    <th>Reason</th>
                                    <th>CE P&L</th>
                                    <th>PE P&L</th>
                                    <th>Total (pts)</th>
                                    <th>P&L (₹)</th>
                                    <th>Cumulative</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(() => {
                                    let cumPnl = 0;
                                    const lotSize = data.lot_size || 1;
                                    return day.trades?.map((t, i) => {
                                      cumPnl += (t.total_pnl || 0);
                                      const tradePnlRs = (t.total_pnl || 0) * lotSize;
                                      const cumPnlRs = cumPnl * lotSize;
                                      return (
                                    <tr key={i}>
                                      <td>{t.trade_no}</td>
                                      <td className="strike-cell">{t.ce_strike || t.strike}</td>
                                      <td className="strike-cell">{t.pe_strike || t.strike}</td>
                                      <td className="time-cell">{t.entry_time}</td>
                                      <td>{t.ce_entry ? t.ce_entry.toFixed(2) : '—'}</td>
                                      <td>{t.pe_entry ? t.pe_entry.toFixed(2) : '—'}</td>
                                      <td className="time-cell">{t.exit_time}</td>
                                      <td>{t.ce_exit ? t.ce_exit.toFixed(2) : '—'}</td>
                                      <td>{t.pe_exit ? t.pe_exit.toFixed(2) : '—'}</td>
                                      <td className="reason-cell">{t.exit_reason}</td>
                                      <td className={t.ce_pnl >= 0 ? 'positive' : 'negative'}>
                                        {t.ce_entry ? ((t.ce_pnl >= 0 ? '+' : '') + t.ce_pnl?.toFixed(2)) : '—'}
                                      </td>
                                      <td className={t.pe_pnl >= 0 ? 'positive' : 'negative'}>
                                        {t.pe_entry ? ((t.pe_pnl >= 0 ? '+' : '') + t.pe_pnl?.toFixed(2)) : '—'}
                                      </td>
                                      <td className={`total-cell ${t.total_pnl >= 0 ? 'positive' : 'negative'}`}>
                                        {t.total_pnl >= 0 ? '+' : ''}{t.total_pnl?.toFixed(2)}
                                      </td>
                                      <td className={tradePnlRs >= 0 ? 'positive' : 'negative'}>
                                        ₹{tradePnlRs >= 0 ? '+' : ''}{Math.round(tradePnlRs).toLocaleString('en-IN')}
                                      </td>
                                      <td className={`total-cell ${cumPnlRs >= 0 ? 'positive' : 'negative'}`}>
                                        ₹{cumPnlRs >= 0 ? '+' : ''}{Math.round(cumPnlRs).toLocaleString('en-IN')}
                                      </td>
                                    </tr>
                                      );
                                    });
                                  })()}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  {data.days?.filter(d => d.status === 'ok').length > 1 && (
                    <tr className="total-row">
                      <td><strong>TOTAL</strong></td>
                      <td colSpan="4"></td>
                      <td></td>
                      <td className={`total-cell ${data.aggregate.pnl_per_lot >= 0 ? 'positive' : 'negative'}`}>
                        <strong>{data.aggregate.pnl_per_lot >= 0 ? '+' : ''}{data.aggregate.pnl_per_lot?.toFixed(2)}</strong>
                      </td>
                      <td className={`total-cell ${data.aggregate.pnl_total >= 0 ? 'positive' : 'negative'}`}>
                        <strong>₹{data.aggregate.pnl_total >= 0 ? '+' : ''}{data.aggregate.pnl_total?.toLocaleString('en-IN')}</strong>
                      </td>
                      <td></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
